import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import pt from 'prop-types';
import useSWR from 'swr';
import { debounce } from 'lodash';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  LinearProgress as MuiLinearProgress,
  Fade,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosCancelToken, dataFetcher, endpoints as apiEndpoints } from 'Api';
import { AppContext } from 'Context';
import { ReactComponent as AddPersonIcon } from 'Assets/add-person.svg';
import { ReactComponent as LinkIcon } from 'Assets/link.svg';
import AlterImage from 'Assets/alter-image.png';

import { Snackbar } from '../snackbar/index';
import styles from './index.module.css';
import SearchInput from './components/SearchInput';
import NewSearchInput from './components/NewSearchInput';
import PersonButton from './components/PersonButton';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from './components/CustomDesigns';

export const ShareScreen = ({
  toastForGroup,
  actionButtonLoadingText,
  actionButtonText,
  apiEndpoint,
  errorText,
  hasLink,
  hideContent,
  imageEditable,
  inputPlaceholder,
  isVisible,
  linkDescription,
  linkTitle,
  multipleSelect,
  notes,
  noUsersFoundText,
  onContinue,
  onHide: closeHandler,
  requireTitle,
  successText,
  title,
  titleEditable,
  titlePlaceholder,
  tempGroupTitle,
  oldGroupMember,
  oldMembersData,
  grandAccess,
  subTitle,
  isViewerInvite,
  darkTheme,
}) => {
  const defaultSnackbar = {
    isVisible: false,
    type: undefined,
    message: undefined,
  };
  const { setAppSnackbar, userDetails, langText } = useContext(AppContext);
  const commonText = langText?.common;
  // console.log('Current user email:', userDetails?.email);
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [titleName, setTitleName] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [emailToSearch, setEmailToSearch] = useState('');
  const [searchingUsers, setUserSearchingStatus] = useState(false);
  const [cancelToken, setCancelToken] = useState(undefined);
  const [selectedUsers, updateSelectedUsers] = useState([]);
  const [operationStatus, setOperationStatus] = useState(undefined);
  const [userList, setUserList] = useState([]);
  // useEffect(() => {
  //   let oldMembers = [];
  //   oldMembers = oldGroupMember || [];
  //   updateSelectedUsers([...oldMembers]);
  // }, [oldGroupMember]);
  // console.log('oldmembers', oldGroupMember);

  const [handlingAction, setActionHandlingStatus] = useState(false);
  const [duplicateErrorType, setDuplicateErrorType] = useState(null); // 'member', 'pending', or null
  const [isSelfSelected, setIsSelfSelected] = useState(false);
  const [activeViewers, setActiveViewers] = useState([]);
  const emailRegEx = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const location = useLocation();

  // Fetch active viewers list when the add members popup is visible (for groups)
  useEffect(() => {
    // eslint-disable-next-line no-console
    // console.log('🔵 ShareScreen Debug:', {
    //   isVisible,
    //   hideContent,
    //   pathname: location.pathname,
    //   search: location.search,
    //   activeViewersCount: activeViewers.length,
    // });

    if (isVisible && !hideContent && location.pathname?.includes('/viewers/groups')) {
      // eslint-disable-next-line no-console
      console.log('🟡 Fetching active viewers...');
      dataFetcher(apiEndpoints.getViewersList, {})
        .then((res) => {
          // eslint-disable-next-line no-console
          console.log('🟢 Active Viewers API Response:', { success: res?.success, dataLength: res?.data?.length, data: res?.data });
          if (res?.success && res?.data) {
            setActiveViewers(res.data);
          } else {
            setActiveViewers([]);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log('🔴 Active Viewers API Error:', err);
          setActiveViewers([]);
        });
    }
  }, [isVisible, hideContent, location.pathname]);
  const onHide = () => {
    setOperationStatus(undefined);
    if (!handlingAction) {
      setEmailToSearch('');
      setImageBase64('');
      updateSelectedUsers([]);
      setUserSearchingStatus(false);
      setUserList([]);
      setDuplicateErrorType(null);
      setIsSelfSelected(false);
      if (closeHandler) {
        closeHandler();
      }
    }
  };
  const selectUser = (userData) => {
    updateSelectedUsers(
      multipleSelect ? [...selectedUsers, userData] : [userData]
    );
  };
  const removeUser = (userData) => {
    updateSelectedUsers(
      selectedUsers.filter(
        (selectedUserData) => selectedUserData.id !== userData.id
      )
    );
  };

  const { mutate: searchUsersByEmail } = useSWR(
    [apiEndpoint, emailToSearch, cancelToken?.token],
    {
      fetcher: (url, inputEmail, uniqueCancelToken) => dataFetcher(url, { email: inputEmail }, uniqueCancelToken),
      onSuccess: ({ success, data, message }) => {
        // eslint-disable-next-line no-console
        console.log('🔍 Search API Response:', { success, dataLength: data?.length, message, emailToSearch });
        setUserSearchingStatus(false);
        if (success) {
          if (emailToSearch !== '') {
            const ids = selectedUsers?.map((user) => user.id);
            const newData = data?.filter((item) => !ids.includes(item.id));
            // eslint-disable-next-line no-console
            console.log('📊 Filtered data:', { originalLength: data?.length, filteredLength: newData?.length });

            // Check if user has status and show appropriate message
            if (newData && newData.length > 0) {
              const firstUser = newData[0];

              // Check status and show toast message
              // Status is null or undefined - show user in list
              if (firstUser.status === null || firstUser.status === undefined) {
                // eslint-disable-next-line no-console
                console.log('✅ Showing real user data');
                setUserList(newData?.slice(0, 5));
              } else if (firstUser.status === '0' || firstUser.status === 0) {
                // User is already a viewer
                if (setAppSnackbar) {
                  setAppSnackbar({
                    isVisible: true,
                    type: 'error',

                    message: 'Already requested this projector - Awaiting response',
                  });
                }
                setUserList([]);
              } else if (firstUser?.status === '1' || firstUser?.status === 1) {
                // Already requested - pending
                if (setAppSnackbar) {
                  setAppSnackbar({
                    isVisible: true,
                    type: 'error',

                    message: 'You are already a viewer',
                  });
                }
                setUserList([]);
              } else if (firstUser?.status === '3' || firstUser?.status === 3) {
                if (setAppSnackbar) {
                  setAppSnackbar({
                    isVisible: true,
                    type: 'error',
                    message: 'Request pending',
                  });
                }
                setUserList([]);
              } else {
                // Unknown status
                setUserList([]);
              }
            } else if (
              // No matching users found - show fallback email for groups/grant-access
              // ONLY when API explicitly says no user found
              (message === 'No Matching user found' || message === 'User Not Exist')
              && (location.pathname === '/viewers/groups' || grandAccess)
              && emailRegEx.test(emailToSearch)
            ) {
              // eslint-disable-next-line no-console
              console.log('⚠️ Showing fallback (typed email only) - No user found in system');
              const list = {
                email: emailToSearch,
                id: emailToSearch,
                status: null,
                firstname: '',
                lastname: '',
              };
              setUserList([list]);
            } else {
              // If no data or empty after filtering, show "No matching user" message
              // eslint-disable-next-line no-console
              console.log('❌ No data to show - clearing list');
              if (data && data.length > 0 && data[0]?.status !== null && data[0]?.status !== undefined) {
                // User was found but status prevents showing them - message already shown above
                setUserList([]);
              } else {
                // No user found at all
                setAppSnackbar({
                  isVisible: true,
                  type: 'error',
                  message: 'No matching user',
                });
                setUserList([]);
              }
            }
          }
        } else if (emailToSearch && emailRegEx.test(emailToSearch)) {
          // API call failed, but still append email if valid for groups/grant-access
          if (location.pathname === '/viewers/groups' || grandAccess) {
            const list = {
              email: emailToSearch,
              id: emailToSearch,
              status: null,
              firstname: '',
              lastname: '',
            };
            setUserList([list]);
          } else {
            setUserList([]);
          }
        } else {
          setUserList([]);
        }
      },
      onError: () => {
        setUserSearchingStatus(false);
        setUserList([]);
      },
      // Disable caching to always fetch fresh data
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0, // Disable deduplication to ensure fresh fetches
      revalidateIfStale: true, // Always refetch if data is stale
    }
  );

  const searchInputHandler = debounce(
    ({ target: { value: inputEmail } }) => {
      if (inputEmail) {
        setEmailToSearch(inputEmail);
      } else {
        setEmailToSearch('');
        setUserList(undefined);
      }
    },
    750,
    { trailing: true }
  );

  const searchUsers = ({ target: { value: inputEmail } }) => {
    if (
      inputEmail
      && (location.pathname.includes('/successors')
        || location.pathname.includes('/groups')
        || location?.search?.includes('create-group'))
    ) {
      setEmailToSearch(inputEmail);
    } else if (inputEmail && emailRegEx.test(inputEmail)) {
      setEmailToSearch(inputEmail);
    } else {
      setEmailToSearch('');
      setUserList([]);
    }
  };
  const removeUserFromList = (selectedUser) => {
    // eslint-disable-next-line no-console
    console.log('❌ Removing user from selection (Cross icon clicked):', {
      removedUser: selectedUser,
      currentSelectedUsers: selectedUsers,
    });
    const newSelectedUsers = selectedUsers.filter(
      (selectedUserData) => selectedUserData.id !== selectedUser.id
    );
    // eslint-disable-next-line no-console
    console.log('📋 Updated selected users after removal:', newSelectedUsers);
    updateSelectedUsers(newSelectedUsers);

    // Re-check if user still has themselves selected after removal
    const currentUserEmail = userDetails?.email?.toLowerCase();
    const isSelf = newSelectedUsers.some((user) => user.email?.toLowerCase() === currentUserEmail);
    setIsSelfSelected(isSelf);

    // Re-check for duplicates after removal (only if not self-selected)
    if (!isSelf && oldMembersData) {
      const members = oldMembersData.members || [];
      const pendingMembers = oldMembersData.pendingMembers || [];

      const hasMemberDuplicate = newSelectedUsers.some((user) => members.some((member) => member.email === user.email || member.id === user.id));
      const hasPendingDuplicate = newSelectedUsers.some((user) => pendingMembers.some((member) => member.email === user.email || member.id === user.id));

      if (hasMemberDuplicate) {
        setDuplicateErrorType('member');
      } else if (hasPendingDuplicate) {
        setDuplicateErrorType('pending');
      } else {
        setDuplicateErrorType(null);
      }
    } else {
      setDuplicateErrorType(null);
    }
  };

  const selectUserFromList = (selectedUser) => {
    const newSelectedUsers = multipleSelect ? [...selectedUsers, selectedUser] : [selectedUser];
    updateSelectedUsers(newSelectedUsers);

    // Check if user is trying to add themselves
    const currentUserEmail = userDetails?.email?.toLowerCase();
    const isSelf = newSelectedUsers.some((user) => user.email?.toLowerCase() === currentUserEmail);
    setIsSelfSelected(isSelf);

    // Check if any newly selected user is a duplicate (only if not self-selected)
    if (!isSelf && oldMembersData) {
      const members = oldMembersData.members || [];
      const pendingMembers = oldMembersData.pendingMembers || [];

      const hasMemberDuplicate = newSelectedUsers.some((user) => members.some((member) => member.email === user.email || member.id === user.id));
      const hasPendingDuplicate = newSelectedUsers.some((user) => pendingMembers.some((member) => member.email === user.email || member.id === user.id));

      if (hasMemberDuplicate) {
        setDuplicateErrorType('member');
      } else if (hasPendingDuplicate) {
        setDuplicateErrorType('pending');
      } else {
        setDuplicateErrorType(null);
      }
    } else if (isSelf) {
      setDuplicateErrorType(null);
    }
  };
  // Debounce API calls - only call after user stops typing for 500ms
  useEffect(() => {
    const emailRegEx = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

    // Don't show any suggestions while user is typing
    if (emailToSearch && emailRegEx.test(emailToSearch)) {
      // Clear suggestions immediately when user types
      setUserList([]);

      // Debounce the API call - wait 500ms after user stops typing
      const debounceTimer = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log('🚀 Calling API for:', emailToSearch);
        setUserSearchingStatus(true);
        searchUsersByEmail();
      }, 500);

      // Cleanup: cancel the API call if user keeps typing
      return () => {
        clearTimeout(debounceTimer);
      };
    }

    if (!emailToSearch) {
      // Clear suggestions when search is empty
      setUserList([]);
    }
    return undefined;
  }, [emailToSearch, setUserSearchingStatus, searchUsersByEmail]);

  useEffect(() => {
    if (operationStatus === true) {
      setAppSnackbar({ isVisible: true, message: successText });
    } else if (operationStatus === false) {
      setAppSnackbar({ isVisible: true, type: 'error', message: errorText });
    }
  }, [operationStatus, errorText, successText, setAppSnackbar]);

  // The naming step is create-group's first screen: name the group, then move on
  // to picking members. Its field lives in the body as a labelled form field.
  const isNameStep = Boolean(hideContent && titleEditable);
  // The picker body only exists for the group flow, which preloads the projector's
  // viewers. Everywhere else the modal is header + search + actions, with no body.
  const showViewerList = activeViewers.length > 0
    && Boolean(location.pathname?.includes('/viewers/groups'));
  const submitFromField = () => {
    const isDisabled = actionButtonText === 'Create Group'
      ? requireTitle || !titleName || (!hideContent && selectedUsers?.length <= 0) || handlingAction
      : (requireTitle && !titleName) || (!hideContent && selectedUsers?.length <= 0) || handlingAction;

    if (!isDisabled) {
      onContinue([
        onHide,
        setActionHandlingStatus,
        setOperationStatus,
        updateSelectedUsers,
        setUserList,
        selectedUsers,
        titleName,
        imageBase64,
      ]);
    }
  };
  const titleField = titleEditable ? (
    <input
      className={styles.nameField}
      id="share-screen-title-input"
      disabled={handlingAction || !hideContent}
      onInput={({ target: { value: inputTitle } }) => { setTitleName(inputTitle); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submitFromField();
        }
      }}
      placeholder={titlePlaceholder}
      maxLength={70}
      type="text"
      autoFocus
    />
  ) : null;

  return (
    <Dialog
      className={cn(styles.shareScreen, {
        [styles.viewerInviteModal]: isViewerInvite || location.pathname?.includes('/viewers/groups'),
        // Opt-in frosted-dark skin — used by the dark pages (/access, /profile)
        // so the modal matches their frosted-dark panels.
        [styles.darkTheme]: darkTheme,
      })}
      onClose={onHide}
      open={isVisible}
      TransitionComponent={Fade}
    >
      <DialogTitle className={styles.shareScreenHeading}>
        <button
          className={styles.closeButton}
          onClick={onHide}
          aria-label="Close"
          type="button"
        >
          <CloseIcon className={styles.closeIcon} />
        </button>
        <div className={styles.shareScreenHeadingContent} data-step={hideContent ? 'name' : 'people'}>
          {imageEditable ? (
            <>
              {hideContent && (
                <input
                  accept="image/*"
                  className={styles.hiddenFormField}
                  disabled={handlingAction || !hideContent}
                  id="share-screen-image-input"
                  onChange={({ target: { files } }) => {
                    const reader = new FileReader();
                    const file = files[0];
                    if (file) {
                      reader.readAsDataURL(file);
                    }
                    reader.addEventListener(
                      'load',
                      () => {
                        setImageBase64(reader.result);
                      },
                      false
                    );
                  }}
                  type="file"
                />
              )}
              <div className={styles.addPersonIconContainer}>
                {imageBase64 ? (
                  <>
                    <label
                      className={styles.addPersonImageContainer}
                      htmlFor="share-screen-image-input"
                    >
                      <img
                        alt="Group Icon"
                        className={styles.imagePreview}
                        src={imageBase64 || AlterImage}
                        onError={(e) => {
                          e.target.src = AlterImage;
                        }}
                      />
                    </label>
                    {!handlingAction && hideContent && (
                      <button
                        className={styles.imageDeleteButton}
                        onClick={() => {
                          document.getElementById(
                            'share-screen-image-input'
                          ).value = '';
                          setImageBase64(undefined);
                        }}
                      />
                    )}
                  </>
                ) : (
                  <label
                    className={cn({
                      [styles.addPersonImageContainer]: true,
                      [styles.hasBG]: true,
                      // Only a real upload target while the file input exists.
                      [styles.uploadable]: hideContent,
                    })}
                    htmlFor="share-screen-image-input"
                    title={hideContent ? 'Add a group image (optional)' : undefined}
                  >
                    <AddPersonIcon className={styles.addPersonIcon} />
                  </label>
                )}
              </div>
            </>
          ) : (
            <div className={styles.addPersonIconContainer}>
              <div
                className={cn({
                  [styles.addPersonImageContainer]: true,
                  [styles.hasBG]: true,
                })}
              >
                <MailOutlineIcon className={styles.addPersonIcon} />
              </div>
            </div>
          )}
          <div className={styles.headText}>
            {isNameStep ? (
              <div className={styles.headTitle}>Create a group</div>
            ) : subTitle ? (
              <>
                <div className={styles.headTitle}>{title}</div>
                <div className={styles.subTitle}>{subTitle}</div>
              </>
            ) : (
              // Past the naming step the name is settled, so show it as text.
              // (Reading it from `titleName` rather than leaving a disabled input
              // mounted — the field moves between steps, so its DOM value would
              // be dropped on remount.)
              <div className={styles.headTitle}>{title || titleName}</div>
            )}
            {/* One-line hint under the title, so the modal explains the step it
                is on without adding a second heading block. */}
            {!subTitle && (
              <div className={styles.headHint}>
                {isNameStep
                  ? 'Name it now — you can add members next'
                  : 'Search by name or email, then pick who to add'}
              </div>
            )}
          </div>
        </div>
        {!hideContent && (
          <NewSearchInput
            handlingAction={handlingAction}
            searchInputHandler={searchInputHandler}
            setUserSearchingStatus={setUserSearchingStatus}
            cancelToken={cancelToken}
            setEmailToSearch={setEmailToSearch}
            setUserList={setUserList}
            setCancelToken={setCancelToken}
            AxiosCancelToken={AxiosCancelToken}
            inputPlaceholder={inputPlaceholder}
            selectedUsers={selectedUsers}
            isDisabled={handlingAction}
            removeUser={removeUser}
            isSelected
            grandAccess
            searchUsers={searchUsers}
            userList={userList}
            searchingUsers={searchingUsers}
            selectUser={selectUser}
            selectUserFromList={selectUserFromList}
            removeUserFromList={removeUserFromList}
            emailToSearch={emailToSearch}
            darkTheme={darkTheme}
            duplicateErrorType={duplicateErrorType}
            isSelfSelected={isSelfSelected}
            onEnterPress={() => {
              const isDisabled = actionButtonText === 'Create Group'
                ? requireTitle || !titleName || (!hideContent && selectedUsers?.length <= 0) || handlingAction
                : (requireTitle && !titleName) || (!hideContent && selectedUsers?.length <= 0) || handlingAction;

              if (!isDisabled) {
                // eslint-disable-next-line no-console
                console.log('👥 Adding users to backend (via Search Enter):', {
                  count: selectedUsers?.length,
                  users: selectedUsers,
                  titleName,
                });
                onContinue([
                  onHide,
                  setActionHandlingStatus,
                  setOperationStatus,
                  updateSelectedUsers,
                  setUserList,
                  selectedUsers,
                  titleName,
                  imageBase64,
                ]);
              }
            }}
          />
        )}
        {/* {searchingUsers && <LinearProgress className={styles.loader} />} */}
      </DialogTitle>

      {!hideContent && showViewerList && (
        <DialogContent className={styles.shareScreenContent}>
          {(() => {
            const visibleViewers = activeViewers
              .filter((viewer) => !selectedUsers.some((sel) => sel.id === viewer.id || sel.email === viewer.email))
              .filter((viewer) => {
                if (!emailToSearch || emailToSearch.trim().length === 0) return true;
                const search = emailToSearch.toLowerCase();
                return viewer.firstname?.toLowerCase().includes(search)
                  || viewer.lastname?.toLowerCase().includes(search)
                  || viewer.email?.toLowerCase().includes(search)
                  || `${viewer.firstname || ''} ${viewer.lastname || ''}`.toLowerCase().includes(search);
              });
            return (
              <div className={styles.activeViewersList}>
                <div className={styles.activeViewersTitle}>
                  <span>Your viewers</span>
                  <span className={styles.activeViewersCount}>{visibleViewers.length}</span>
                </div>
                {visibleViewers.length > 0 ? (
                  <div className={styles.activeViewersItems}>
                    {visibleViewers.map((viewer) => (
                      <PersonButton
                        data={viewer}
                        key={viewer.id}
                        onClick={() => {
                          selectUserFromList(viewer);
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    {emailToSearch
                      ? `No viewer matches “${emailToSearch}”`
                      : 'Everyone from your viewers is already selected'}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      )}

      {/* Naming step — the field gets its own labelled row rather than doubling
          as the modal heading, and the optional cover image is spelled out. */}
      {isNameStep && (
        <DialogContent className={styles.shareScreenContent}>
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel} htmlFor="share-screen-title-input">
              Group name
            </label>
            {titleField}
            <div className={styles.fieldFoot}>
              <span className={styles.fieldHint}>
                Members of this group see everything you share with it.
              </span>
              <span className={styles.fieldCounter}>
                {`${(titleName || '').length}/70`}
              </span>
            </div>
          </div>
        </DialogContent>
      )}

      <DialogActions
        className={styles.shareScreenActions}
        style={{ borderTopLeftRadius: '0', borderTopRightRadius: '0' }}
      >
        <Snackbar
          isVisible={toastForGroup?.message && snackbar?.isVisible}
          message={toastForGroup?.message || '...'}
          onClose={() => {
            setSnackbar(defaultSnackbar);
          }}
          type={snackbar?.type}
        />

        {/* Note Section - Only show for viewer invites */}
        {isViewerInvite && (
          <div className={styles.noteText}>
            <strong>Note:</strong>
            {' '}
            Invited users will receive email notification
          </div>
        )}

        {/* Selection count — only once something is picked; an empty-state count
            is noise the primary button already conveys. */}
        {!hideContent && selectedUsers?.length > 0 && (
          <div className={styles.selectionCount}>
            {`${selectedUsers.length} selected`}
          </div>
        )}

        <button
          type="button"
          className={styles.shareScreenCancelButton}
          onClick={onHide}
          disabled={handlingAction}
        >
          {commonText?.cancel || 'Cancel'}
        </button>

        {/* Plain <button> rather than the shared <Button isBlue>: that component
            styles from its own CSS module, so which stylesheet won depended on
            bundle import order — which is what left this action looking washed
            out / permanently disabled. */}
        <button
          type="button"
          className={styles.shareScreenDoneButton}
          disabled={
            actionButtonText === 'Create Group'
              ? requireTitle
              || !titleName
              || (!hideContent && selectedUsers?.length <= 0)
              || handlingAction
              : (requireTitle && !titleName)
              || (!hideContent && selectedUsers?.length <= 0)
              || handlingAction
              || duplicateErrorType !== null
              || isSelfSelected
          }
          onClick={() => {
            // eslint-disable-next-line no-console
            console.log('👥 Adding users to backend:', {
              count: selectedUsers?.length,
              users: selectedUsers,
              titleName,
            });
            onContinue([
              onHide,
              setActionHandlingStatus,
              setOperationStatus,
              updateSelectedUsers,
              setUserList,
              selectedUsers,
              titleName,
              imageBase64,
            ]);
          }}
        >
          {handlingAction && <span className={styles.buttonSpinner} aria-hidden="true" />}
          {handlingAction ? actionButtonLoadingText : actionButtonText}
        </button>
      </DialogActions>

      {hasLink && (
        <DialogActions
          className={cn({
            [styles.shareScreenActions]: true,
            [styles.linkShare]: true,
          })}
        >
          <div className={styles.shareScreenHeadingContent}>
            <LinkIcon className={styles.linkIcon} />
            <>{linkTitle}</>
          </div>
          <div className={styles.shareScreenLinkText}>{linkDescription}</div>
          <div className={styles.shareScreenLinkButton}>Copy Link</div>
        </DialogActions>
      )}
    </Dialog>
  );
};
